/**
 * SubtitleStyleDialog - 字幕样式配置对话框
 *
 * 功能：整合所有字幕组件，提供完整的字幕配置界面
 * 设计：参考 BgMusicDialog 的样式和布局
 * 特点：独立组件，极小侵入性
 */

import React, { useState, useEffect, useCallback } from 'react';
import { X, Type } from 'lucide-react';
import { getSubtitleTemplates } from '../../utils/api';
import type { SubtitleStyle, SubtitleTemplate } from '../../types';
import TemplateSelector from './TemplateSelector';
import StyleConfigPanel from './StyleConfigPanel';
import LivePreview from './LivePreview';

interface SubtitleStyleDialogProps {
  /** 对话框是否打开 */
  isOpen: boolean;
  /** 关闭对话框回调 */
  onClose: () => void;
  /** 应用字幕样式回调 */
  onApply: (style: SubtitleStyle, templateId?: string) => void;
  /** 当前字幕样式（可选，用于编辑已有样式） */
  currentStyle?: SubtitleStyle;
  /** 翻译函数 */
  t?: (key: string) => string;
}

type EditorMode = 'template' | 'custom';

const SubtitleStyleDialog: React.FC<SubtitleStyleDialogProps> = ({
  isOpen,
  onClose,
  onApply,
  currentStyle,
  t = (key: string) => key
}) => {
  // 编辑器模式：模板选择或自定义
  const [mode, setMode] = useState<EditorMode>('template');

  // 选中的模板ID
  const [selectedTemplateId, setSelectedTemplateId] = useState<string>('default');

  // 模板列表
  const [templates, setTemplates] = useState<SubtitleTemplate[]>([]);

  // 自定义样式
  const [customStyle, setCustomStyle] = useState<SubtitleStyle>({
    fontName: 'Arial',
    fontSize: 20,
    primaryColor: '#FFFFFF',
    outlineColor: '#000000',
    outlineWidth: 2,
    alignment: 'bottom',
    marginV: 20,
    bold: false,
    italic: false
  });

  // 防抖计时器
  const [debounceTimer, setDebounceTimer] = useState<NodeJS.Timeout | null>(null);

  /**
   * 初始化：始终显示"选择模板"模式
   */
  useEffect(() => {
    if (currentStyle) {
      // 如果有当前样式，设置到自定义样式，但不切换模式
      setCustomStyle(currentStyle);
      // 🔧 修复：始终默认显示"选择模板"模式，不自动切换到"自定义样式"
      // setMode('custom');  // ← 移除这行
    }
    // 每次打开对话框时重置为"选择模板"模式
    setMode('template');
  }, [currentStyle, isOpen]);  // ← 添加 isOpen 依赖，每次打开都重置

  /**
   * 加载模板列表并匹配当前样式
   */
  useEffect(() => {
    const loadTemplatesAndMatch = async () => {
      if (!isOpen) return;

      try {
        const response = await getSubtitleTemplates();
        const templateList = response.templates || [];
        setTemplates(templateList);

        // 如果有当前样式且包含 templateId，直接使用
        if (currentStyle?.templateId) {
          const matchedTemplate = templateList.find((t: SubtitleTemplate) => t.id === currentStyle.templateId);
          if (matchedTemplate) {
            console.log('🎯 通过 templateId 匹配到字幕模板:', matchedTemplate.id, matchedTemplate.name);
            setSelectedTemplateId(matchedTemplate.id);
            return;
          } else {
            console.warn('⚠️ templateId 不存在:', currentStyle.templateId);
          }
        }

        // 如果没有 templateId 或 templateId 无效，通过样式属性匹配
        if (currentStyle) {
          const matchedTemplate = templateList.find((template: SubtitleTemplate) => {
            const style = template.style;
            return (
              style.fontName === currentStyle.fontName &&
              style.fontSize === currentStyle.fontSize &&
              style.primaryColor === currentStyle.primaryColor &&
              style.outlineColor === currentStyle.outlineColor &&
              style.outlineWidth === currentStyle.outlineWidth &&
              style.alignment === currentStyle.alignment &&
              style.marginV === currentStyle.marginV &&
              style.bold === currentStyle.bold &&
              style.italic === currentStyle.italic
            );
          });

          if (matchedTemplate) {
            console.log('🎯 通过样式匹配到字幕模板:', matchedTemplate.id, matchedTemplate.name);
            setSelectedTemplateId(matchedTemplate.id);
          } else {
            console.log('⚠️ 未匹配到字幕模板，使用默认');
            setSelectedTemplateId('default');
          }
        }
      } catch (error) {
        console.error('❌ 加载字幕模板列表失败:', error);
        setTemplates([]);
      }
    };

    loadTemplatesAndMatch();
  }, [isOpen, currentStyle]);

  /**
   * 防抖更新样式（300ms）
   */
  const debouncedUpdateStyle = useCallback((style: SubtitleStyle) => {
    if (debounceTimer) {
      clearTimeout(debounceTimer);
    }

    const timer = setTimeout(() => {
      setCustomStyle(style);
    }, 300);

    setDebounceTimer(timer);
  }, [debounceTimer]);

  /**
   * 选择模板
   */
  const handleSelectTemplate = (template: SubtitleTemplate) => {
    setSelectedTemplateId(template.id);
    // 保存 templateId 到样式中
    setCustomStyle({ ...template.style, templateId: template.id });
    setMode('template');
  };

  /**
   * 应用字幕样式
   */
  const handleApply = () => {
    onApply(customStyle);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50"
      onClick={onClose}
    >
      <div
        className="relative bg-gradient-to-r from-blue-500/20 via-purple-500/20 to-pink-500/20 backdrop-blur-md rounded-2xl p-4 shadow-2xl overflow-hidden w-[750px] h-[650px] max-h-[90vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* 标题栏 */}
        <div className="flex items-center justify-between mb-3 border-b border-gray-700/50 pb-2 flex-shrink-0">
          <h2 className="text-lg font-semibold text-white flex items-center gap-2">
            <Type className="w-4 h-4" />
            {t('subtitle.configTitle')}
          </h2>
          <button
            className="text-gray-300 hover:text-white transition-colors"
            onClick={onClose}
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* 内容区 */}
        <div className="bg-gray-800/95 backdrop-blur-sm rounded-xl p-4 flex-1 overflow-auto mb-3">
          {/* 编辑模式切换 */}
          <div className="flex gap-2 mb-4">
            <button
              onClick={() => setMode('template')}
              className={`flex-1 py-1.5 px-3 rounded-lg text-sm font-medium transition-colors ${
                mode === 'template'
                  ? 'bg-blue-500 text-white'
                  : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
              }`}
            >
              {t('subtitle.selectTemplate')}
            </button>
            <button
              onClick={() => setMode('custom')}
              className={`flex-1 py-1.5 px-3 rounded-lg text-sm font-medium transition-colors ${
                mode === 'custom'
                  ? 'bg-blue-500 text-white'
                  : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
              }`}
            >
              {t('subtitle.customStyle')}
            </button>
          </div>

          {/* 模板选择模式 */}
          {mode === 'template' && (
            <div className="space-y-4">
              <TemplateSelector
                selectedTemplateId={selectedTemplateId}
                onSelectTemplate={handleSelectTemplate}
                previewMode="mini"
                t={t}
              />

              {/* 预览区 */}
              <div className="border-t border-gray-700/50 pt-3">
                <h3 className="text-xs font-semibold text-gray-300 mb-2">
                  {t('subtitle.preview')}
                </h3>
                <div className="flex justify-center">
                  <LivePreview style={customStyle} text={t('subtitle.previewText')} />
                </div>
              </div>
            </div>
          )}

          {/* 自定义样式模式 */}
          {mode === 'custom' && (
            <div className="grid grid-cols-2 gap-4">
              {/* 左侧：配置面板 */}
              <div>
                <StyleConfigPanel
                  style={customStyle}
                  onStyleChange={debouncedUpdateStyle}
                  t={t}
                />
              </div>

              {/* 右侧：预览 */}
              <div className="border-l border-gray-700/50 pl-4">
                <h3 className="text-xs font-semibold text-gray-300 mb-2">
                  {t('subtitle.livePreview')}
                </h3>
                <LivePreview style={customStyle} text={t('subtitle.previewText')} />
              </div>
            </div>
          )}
        </div>

        {/* 底部按钮 */}
        <div className="flex justify-end gap-3">
          <button
            onClick={onClose}
            className="px-4 py-1.5 bg-gray-700 hover:bg-gray-600 text-white text-sm rounded-lg transition-colors"
          >
            {t('common.cancel')}
          </button>
          <button
            onClick={handleApply}
            className="px-4 py-1.5 bg-blue-500 hover:bg-blue-600 text-white text-sm rounded-lg transition-colors"
          >
            {t('common.apply')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default SubtitleStyleDialog;
